import { useReducer } from 'react';
import { useQuery } from '@/hooks';
import { ScoredEvent } from '@/types/event';
import { PromptAttachmentType } from '@/types/prompt';
import { toast } from '@/components';
import { type ReasoningStep } from '@/components';
import { DEFAULT_CONFIDENCE_THRESHOLD } from '@/utils/query-filters';

const SIMULATION_TOP_K = 32;
const MAX_SIMULATION_RESULTS = 12;
const SIMULATION_TOP_RESULTS = 4;

function pickSpread(
    ranked: ScoredEvent[],
    count: number,
    head: number,
): ScoredEvent[] {
    if (ranked.length <= count) return ranked;
    const tail = ranked.slice(head);
    const picks = count - head;
    const step = (tail.length - 1) / Math.max(picks - 1, 1);
    return [
        ...ranked.slice(0, head),
        ...Array.from({ length: picks }, (_, i) => tail[Math.round(i * step)]),
    ];
}

interface SimulationParams {
    promptText: string;
    sourceIds: string[];
    attachment?: { url: string; type?: string };
}

interface SimulationState {
    events: ScoredEvent[];
    sourceCount: number;
}

type SimulationAction =
    | { type: 'reset'; sourceCount: number }
    | { type: 'event_received'; event: ScoredEvent };

const initialState: SimulationState = {
    events: [],
    sourceCount: 0,
};

function simulationReducer(
    state: SimulationState,
    action: SimulationAction,
): SimulationState {
    switch (action.type) {
        case 'reset':
            return { ...initialState, sourceCount: action.sourceCount };
        case 'event_received':
            return { ...state, events: [...state.events, action.event] };
    }
}

function deriveSteps(
    { events, sourceCount }: SimulationState,
    uniqueSourceCount: number,
    isLoading: boolean,
    t: (key: string, options?: Record<string, unknown>) => string,
): ReasoningStep[] {
    if (sourceCount === 0) return [];

    const steps: ReasoningStep[] = [
        { text: t('simulation.scanningEvents', { count: sourceCount }) },
    ];

    if (!isLoading && events.length === 0) {
        steps.push({ text: t('simulation.noResults') });
    } else if (events.length > 0) {
        steps.push({
            text: t('simulation.displayingEvents', {
                events: t('simulation.eventCount', {
                    count: events.length,
                }),
                sources: t('simulation.uniqueSourceCount', {
                    count: uniqueSourceCount,
                }),
            }),
        });
    }

    return steps;
}

export function useSimulation(
    t: (key: string, options?: Record<string, unknown>) => string,
) {
    const [state, dispatch] = useReducer(simulationReducer, initialState);
    const {
        execute: executeQuery,
        cancel,
        isLoading: isSimulating,
    } = useQuery();

    const ranked = [...state.events].sort(
        (a, b) => b.confidence - a.confidence,
    );
    const visibleEvents = pickSpread(
        ranked,
        MAX_SIMULATION_RESULTS,
        SIMULATION_TOP_RESULTS,
    );

    const uniqueSourceCount = new Set(
        visibleEvents.map((e) => e.event.sourceId),
    ).size;
    const simulationSteps = deriveSteps(
        { events: visibleEvents, sourceCount: state.sourceCount },
        uniqueSourceCount,
        isSimulating,
        t,
    );

    const runSimulation = async ({
        promptText,
        sourceIds,
        attachment,
    }: SimulationParams) => {
        if (sourceIds.length === 0 || (!promptText.trim() && !attachment)) {
            return;
        }

        dispatch({ type: 'reset', sourceCount: sourceIds.length });

        const queryAttachment = attachment
            ? {
                  url: attachment.url,
                  type: attachment.type as PromptAttachmentType | undefined,
              }
            : undefined;

        try {
            await executeQuery(
                {
                    text: promptText,
                    sourceIds,
                    attachment: queryAttachment,
                    demo: false,
                    topK: SIMULATION_TOP_K,
                    simulation: true,
                    minConfidence: DEFAULT_CONFIDENCE_THRESHOLD,
                },
                {
                    onEventReceived: (event: ScoredEvent) =>
                        dispatch({ type: 'event_received', event }),
                    skipCache: true,
                },
            );
        } catch {
            toast.error(t('toast.simulationError'));
        }
    };

    return {
        simulationEvents: visibleEvents,
        isSimulating,
        simulationSteps,
        runSimulation,
        stopSimulation: cancel,
    };
}
